/**
 * SharingSelector - Side-by-side Groups and Users selection
 * Simplified approach without search API dependency
 */

import React, { useMemo } from 'react';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, Chip, Divider } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import { useMyGroups, useAllGroups } from '../../hooks/api/useGroups';
import { useUsersForSharing } from '../../hooks/api/useUsersForSharing';
import { useRecentGroups, useRecentUsers } from '../../stores/sharingStore';
import { usePermissions } from '../../hooks/permissions/usePermissions';
import { log } from '../../utils/errors/logger';

interface SharingSelectorProps {
  /** Current user IDs (comma-separated string) */
  userIds?: string;
  /** Current group IDs (comma-separated string) */
  groupIds?: string;
  /** Callback when selection changes */
  onChange?: (userIds: string, groupIds: string) => void;
  /** Label to show above the selector */
  label?: string;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Error state */
  error?: boolean;
  /** Helper text */
  helperText?: string;
}

/**
 * Parse comma-separated IDs into array
 */
const parseIds = (ids?: string): string[] => {
  if (!ids) { return []; }
  return ids.split(',').map(id => id.trim()).filter(Boolean);
};

export const SharingSelector: React.FC<SharingSelectorProps> = ({
  userIds = '',
  groupIds = '',
  onChange,
  label,
  disabled = false,
  error = false,
  helperText
}) => {
  const { t } = useTranslation();
  const { can } = usePermissions();

  // Determine if user can see all groups based on permissions
  const canSeeAllGroups = can.canViewAllGroups;

  // Load groups data - use appropriate hook based on user role
  const { data: myGroupsResponse, isLoading: myGroupsLoading } = useMyGroups();
  const { data: allGroupsResponse, isLoading: allGroupsLoading } = useAllGroups(canSeeAllGroups);
  const { data: usersData, isLoading: usersLoading } = useUsersForSharing();

  // Choose appropriate groups data and loading state
  const allGroups = allGroupsResponse?.groups || [];
  const myGroups = myGroupsResponse?.groups || [];
  const userGroups = canSeeAllGroups ? allGroups : myGroups;
  const groupsLoading = canSeeAllGroups ? allGroupsLoading : myGroupsLoading;
  const allUsers = usersData || [];

  // Recently used items from Zustand store
  const { recentlyUsedGroups, addRecentGroup } = useRecentGroups();
  const { recentlyUsedUsers, addRecentUser } = useRecentUsers();

  // Parse current IDs
  const currentUserIds = useMemo(() => parseIds(userIds), [userIds]);
  const currentGroupIds = useMemo(() => parseIds(groupIds), [groupIds]);

  // Debug logging on component render
  console.log('[SharingSelector] Render:', {
    usersLoading,
    allUsersCount: allUsers.length,
    sampleUser: allUsers[0],
    currentUserIds,
    availableUsersWillBe: allUsers.filter(u => !currentUserIds.includes(u.id)).length
  });

  // Handler for group selection
  const handleGroupSelect = (groupId: string) => {
    if (!groupId || currentGroupIds.includes(groupId)) { return; }

    addRecentGroup(groupId);

    const newGroupIds = [...currentGroupIds, groupId];
    const groupIdString = newGroupIds.join(', ');
    const userIdString = currentUserIds.join(', ');

    log.user.action('group_selected', 'SharingSelector', { groupId });
    onChange?.(userIdString, groupIdString);
  };

  // Handler for user selection
  const handleUserSelect = (userId: string) => {
    console.log('[SharingSelector] handleUserSelect called:', {
      userId,
      currentUserIds,
      allUsersCount: allUsers.length,
      allUserIds: allUsers.map(u => u.id),
      willBeBlocked: !userId || currentUserIds.includes(userId)
    });

    if (!userId || currentUserIds.includes(userId)) {
      console.warn('[SharingSelector] Early return - userId empty or already selected');
      return;
    }

    addRecentUser(userId);

    const newUserIds = [...currentUserIds, userId];
    const userIdString = newUserIds.join(', ');
    const groupIdString = currentGroupIds.join(', ');

    console.log('[SharingSelector] Calling onChange with:', { userIdString, groupIdString });
    log.user.action('user_selected', 'SharingSelector', { userId });
    onChange?.(userIdString, groupIdString);
  };

  // Handler for removing group
  const handleRemoveGroup = (groupId: string) => {
    const newGroupIds = currentGroupIds.filter(id => id !== groupId);
    const groupIdString = newGroupIds.join(', ');
    const userIdString = currentUserIds.join(', ');

    log.user.action('group_removed', 'SharingSelector', { groupId });
    onChange?.(userIdString, groupIdString);
  };

  // Handler for removing user
  const handleRemoveUser = (userId: string) => {
    const newUserIds = currentUserIds.filter(id => id !== userId);
    const userIdString = newUserIds.join(', ');
    const groupIdString = currentGroupIds.join(', ');

    log.user.action('user_removed', 'SharingSelector', { userId });
    onChange?.(userIdString, groupIdString);
  };

  // Filter available items
  const recentGroups = userGroups.filter(g => recentlyUsedGroups.includes(g.id) && !currentGroupIds.includes(g.id));
  const availableGroups = userGroups.filter(g => !currentGroupIds.includes(g.id));

  const recentUsers = allUsers.filter(u => recentlyUsedUsers.includes(u.id) && !currentUserIds.includes(u.id));
  const availableUsers = allUsers.filter(u => !currentUserIds.includes(u.id));

  return (
    <Box>
      {label && (
        <Typography variant="subtitle2" gutterBottom>
          {label}
        </Typography>
      )}

      {/* Side-by-side Groups and Users */}
      <Grid container spacing={2}>
        {/* Groups Column */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('documents.sharing.groups', 'Gruppen')} ({currentGroupIds.length})
          </Typography>

          {availableGroups.length > 0 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t('documents.sharing.selectGroups')}</InputLabel>
              <Select
                value=""
                onChange={(e) => handleGroupSelect(e.target.value)}
                disabled={disabled || groupsLoading}
                label={t('documents.sharing.selectGroups')}
                error={error}
              >
                {/* Recently used groups first */}
                {recentGroups.length > 0 && [
                  <MenuItem key="recent-header" disabled>
                    <Typography variant="overline" color="text.secondary">
                      {t('common.recentlyUsed')}
                    </Typography>
                  </MenuItem>,
                  ...recentGroups.map((group) => (
                    <MenuItem key={`recent-${group.id}`} value={group.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>👥</Typography>
                        <Box>
                          <Typography variant="body2">{group.displayName}</Typography>
                          {group.description && (
                            <Typography variant="caption" color="text.secondary">
                              {group.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </MenuItem>
                  )),
                  <Divider key="recent-divider" />
                ]}

                {/* All available groups */}
                <MenuItem disabled>
                  <Typography variant="overline" color="text.secondary">
                    {canSeeAllGroups
                      ? t('documents.sharing.allGroups', 'Alle Gruppen')
                      : t('documents.sharing.myGroups', 'Meine Gruppen')
                    } ({availableGroups.length})
                  </Typography>
                </MenuItem>
                {availableGroups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography>👥</Typography>
                      <Box>
                        <Typography variant="body2">{group.displayName}</Typography>
                        {group.description && (
                          <Typography variant="caption" color="text.secondary">
                            {group.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Selected groups as chips */}
          {currentGroupIds.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {currentGroupIds.map((groupId) => {
                const group = userGroups.find(g => g.id === groupId);
                return (
                  <Chip
                    key={`group-${groupId}`}
                    label={`👥 ${group?.displayName || groupId}`}
                    onDelete={() => handleRemoveGroup(groupId)}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                );
              })}
            </Box>
          )}
        </Grid>

        {/* Users Column */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('documents.sharing.users', 'Benutzer')} ({currentUserIds.length})
          </Typography>

          {allUsers.length > 0 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t('documents.sharing.selectUsers')}</InputLabel>
              <Select
                value=""
                onChange={(e) => handleUserSelect(e.target.value)}
                disabled={disabled || usersLoading}
                label={t('documents.sharing.selectUsers')}
                error={error}
              >
                {/* Recently used users first */}
                {recentUsers.length > 0 && [
                  <MenuItem key="recent-user-header" disabled>
                    <Typography variant="overline" color="text.secondary">
                      {t('common.recentlyUsed')}
                    </Typography>
                  </MenuItem>,
                  ...recentUsers.map((user) => (
                    <MenuItem key={`recent-${user.id}`} value={user.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>👤</Typography>
                        <Box>
                          <Typography variant="body2">{user.display_name}</Typography>
                          {user.email && (
                            <Typography variant="caption" color="text.secondary">
                              {user.email}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </MenuItem>
                  )),
                  <Divider key="recent-user-divider" />
                ]}

                <MenuItem disabled>
                  <Typography variant="overline" color="text.secondary">
                    {t('documents.sharing.allUsers', 'Alle Benutzer')} ({availableUsers.length})
                  </Typography>
                </MenuItem>
                {availableUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography>👤</Typography>
                      <Box>
                        <Typography variant="body2">{user.display_name}</Typography>
                        {user.email && (
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Selected groups as chips */}
          {currentUserIds.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {currentUserIds.map((userId) => {
                const user = allUsers.find(g => g.id === userId);
                return (
                  <Chip
                    key={`user-${userId}`}
                    label={`👤 ${user?.display_name || userId}`}
                    onDelete={() => handleRemoveUser(userId)}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                );
              })}
            </Box>
          )}
        </Grid>
      </Grid>

      {helperText && (
        <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ mt: 1, display: 'block' }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default SharingSelector;